import { CircleAlert } from 'lucide-react';
import React from 'react';

interface DocumentPreviewTabProps {
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  htmlContent?: string;
  hasTemplate: boolean;
}

/**
 * Renders the document preview tab content: loading, error, the rendered HTML
 * template, or empty states. The preview is the server-rendered manufacturer-form
 * HTML template; the generated document asset (xlsx/pdf/docx) is downloaded and
 * emailed separately, not rendered here.
 */
export const DocumentPreviewTab: React.FC<DocumentPreviewTabProps> = ({
  isLoading,
  isError,
  errorMessage,
  htmlContent,
  hasTemplate,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading form template...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <CircleAlert className="w-12 h-12 mb-3 text-destructive mx-auto" />
          <p className="text-destructive font-semibold mb-2">Error loading form</p>
          <p className="text-muted-foreground text-sm">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (htmlContent) {
    return <div className="bg-background" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  }

  if (!hasTemplate) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No document template configured for this program</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">No form data available</p>
    </div>
  );
};
